import React, { useMemo } from "react";
import { Box, TextField, Autocomplete, Button, Grid, Typography, Divider, Tooltip } from "@mui/material";
import { ArrowDropDown } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { ROUTE_CREATE_JOB, ROUTE_VIEW_JOB, ROUTE_COMPANY_JOB_DETAILS } from "../../constants/routes";
import { ACCESS_TOKEN } from "../../constants/AppVarConstants";

interface VideoDetailheaderProps {
  jobDetails?: any;
  jobId?: string;
  profileId?: string;
  organizationJobs?: any[];
  loadingOrganizationJobs?: boolean;
  isAuthenticated?: boolean;
}

const VideoDetailheader = ({ jobDetails, jobId, profileId, organizationJobs = [], loadingOrganizationJobs = false, isAuthenticated = true }: VideoDetailheaderProps) => {
  const navigate = useNavigate();

  // Debug logging
  console.log('VideoDetailheader props:', {
    organizationJobs,
    loadingOrganizationJobs,
    organizationJobsLength: organizationJobs.length
  });

  // Use useMemo to make options reactive to organizationJobs changes
  const options = useMemo(() => {
    console.log('Recalculating options, organizationJobs:', organizationJobs);
    if (organizationJobs.length > 0) {
      const mappedOptions = organizationJobs.map((job: any) => {
        console.log('Processing job:', job);
        return job.jobName || 'Untitled Job';
      });
      console.log('Mapped options:', mappedOptions);
      return mappedOptions;
    }
    
    console.log('No organization jobs, returning empty array');
    return [];
  }, [organizationJobs]);

  console.log('Final options:', options);

  const handleJobSelection = (event: any, newValue: string | null) => {
    if (newValue && organizationJobs.length > 0) {
      const selectedJob = organizationJobs.find((job: any) => 
        (job.title || job.jobTitle || job.jobName || job.job_name || 'Untitled Job') === newValue
      );
      if (selectedJob && selectedJob.jobId) {
        // Navigate to the selected job
        navigate(`/client-job-details/${selectedJob.jobId}`);
      }
    }
  };

  const handleEditJob = () => {

   
    if (profileId && jobId) {
      
     
      navigate(`${ROUTE_VIEW_JOB}?edit=false&jobId=${jobId}`);
      return;
    }

    if (jobId) {
      navigate(`${ROUTE_CREATE_JOB}?edit=true&jobId=${jobId}`);
    } else {
      navigate(ROUTE_CREATE_JOB);
    }
  };

  // Extract data from jobDetails with fallbacks
  const {
    title = "HOME MANAGEMENT",
    functions = "Banking",
    location = "Mumbai,Puna",
    minExperience = "6M",
    maxExperience = "1years",
    preferredQualification = "MBA",
    minCtc = "10Lc",
    maxCtc = "12Lc",
    personalityType = "The Connector"
  } = jobDetails || {};

  console.log('Current job title:', title);
  console.log('Job details:', jobDetails);

  // Format experience range
  const experienceRange = `${minExperience}-${maxExperience}`;
  
  // Format CTC range
  const ctcRange = `${minCtc}-${maxCtc}`;

  return (
    <Box sx={{
      backgroundColor: "#E6E4FE99",
      width: "100%",
      height: "60px",
      display: "flex",
      padding: "0 16px",
      flexDirection: "row",
      alignItems: "center",
      gap: "16px",
      boxSizing: "border-box",
    }}>
      {/* Job Title Autocomplete or SeeVi Logo - Show logo for shareprofile */}
      <Box sx={{ minWidth: "264px", flexShrink: 0, display: "flex", alignItems: "center" }}>
        {profileId ? (
          // Show SeeVi colored logo for shareprofile
          <img 
            src="/images/client-website/seevi-logo.png" 
            alt="SeeVi" 
            style={{ height: "40px", width: "auto" }}
          />
        ) : (
          // Show jobs dropdown for regular job details
          <Autocomplete
            disablePortal
            disableClearable
            disabled={!isAuthenticated}
            options={options}
            value={title}
            onChange={handleJobSelection}
            loading={loadingOrganizationJobs}
            popupIcon={<ArrowDropDown sx={{ color: "#222222", fontSize: "20px" }} />}
            componentsProps={{
              popper: {
                sx: {
                  zIndex: 1300,
                  "& .MuiAutocomplete-listbox": {
                    "& .MuiAutocomplete-option": {
                      fontSize: "13px",
                      fontWeight: 500,
                      fontFamily: "DM Sans",
                      lineHeight: "100%",
                      letterSpacing: "0px",
                      padding: "8px 14px",
                    },
                  },
                },
              },
            }}
            sx={{ 
              backgroundColor: "white", 
              borderRadius: "10px", 
              width: "264px", 
              height: "48px",
              "& .MuiOutlinedInput-root": {
                height: "48px",
                borderRadius: "10px",
                padding: "0 14px",
                display: "flex",
                alignItems: "center",
                "& fieldset": {
                  border: "none",
                },
                "&:hover fieldset": {
                  border: "none",
                },
                "&.Mui-focused fieldset": {
                  border: "none",
                },
              },
              "& .MuiAutocomplete-input": {
                color: "#000000",
                fontWeight: 500,
                fontSize: "12px !important",
                fontFamily: "DM Sans",
                padding: "0 !important",
                textAlign: "left",
                lineHeight: "100%",
                letterSpacing: "0px",
              },
              "& .MuiInputBase-input": {
                fontSize: "14px !important",
                fontWeight: 500,
                fontFamily: "DM Sans",
                lineHeight: "100%",
                letterSpacing: "0px",
              },
              "& .MuiAutocomplete-endAdornment": {
                right: "12px !important",
                position: "absolute !important",
                display: "flex !important",
                visibility: "visible !important",
                opacity: "1 !important",
                pointerEvents: "auto !important",
                "& .MuiAutocomplete-popupIndicator": {
                  display: "flex !important",
                  visibility: "visible !important",
                  opacity: "1 !important",
                  "& .MuiSvgIcon-root": {
                    color: "#222222 !important",
                    fontSize: "20px !important",
                    display: "block !important",
                    visibility: "visible !important",
                    opacity: "1 !important",
                  },
                },
                "& .MuiSvgIcon-root": {
                  color: "#222222 !important",
                  fontSize: "20px !important",
                  display: "block !important",
                  visibility: "visible !important",
                  opacity: "1 !important",
                },
              },
              "& .MuiInputBase-root": {
                paddingRight: "40px !important",
              },
              "& .MuiAutocomplete-popupIndicator": {
                display: "inline-flex !important",
                visibility: "visible !important",
                opacity: "1 !important",
                pointerEvents: "auto !important",
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select Job..."
                contentEditable={false}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {params.InputProps.endAdornment}
                      {!loadingOrganizationJobs && (
                        <Box
                          sx={{
                            position: "absolute",
                            right: "12px",
                            display: "flex",
                            alignItems: "center",
                            pointerEvents: "none",
                          }}
                        >
                          <ArrowDropDown sx={{ color: "#222222", fontSize: "20px" }} />
                        </Box>
                      )}
                    </>
                  ),
                }}
                sx={{
                  "& .MuiInputBase-input": {
                    fontWeight: 500,
                    fontSize: "14px !important",
                    fontFamily: "DM Sans",
                    lineHeight: "100%",
                    letterSpacing: "0px",
                  },
                  "& .MuiInputBase-input::placeholder": {
                    color: "#000000",
                    opacity: "1",
                    fontWeight: 500,
                    fontSize: "14px !important",
                    fontFamily: "DM Sans",
                    lineHeight: "100%",
                    letterSpacing: "0px",
                  },
                }}
                label=""
              />
            )} 
          />
        )}
      </Box>

      {/* Job Details Grid */}
      <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
        <Box sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          gap: "8px",
          flexWrap: "nowrap",
          overflow: "hidden"
        }}>
          {/* Function */}
          <Box sx={{ minWidth: "80px", maxWidth: "120px", textAlign: "center", overflow: "hidden", display: "flex", flexDirection: "column", gap: "12px" }}>
            <Typography 
              variant="body1" 
              sx={{ 
                fontFamily: "DM Sans",
                fontWeight: 500,
                fontSize: "14px",
                lineHeight: "100%",
                letterSpacing: "0px",
                textTransform: "capitalize",
                color: "#756DE9"
              }}
            >
              Function
            </Typography>
            <Tooltip title={functions} arrow placement="top">
              <Typography 
                variant="body2" 
                sx={{ 
                  fontFamily: "DM Sans",
                  fontWeight: 500,
                  fontStyle: "normal",
                  fontSize: "14px",
                  lineHeight: "100%",
                  letterSpacing: "0px",
                  color: "#222222",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  width: "100%"
                }}
              >
                {functions}
              </Typography>
            </Tooltip>
          </Box>

          {/* Vertical Divider */}
          <Divider orientation="vertical" flexItem sx={{ height: "40px", mx: 1 }} />

          {/* Location */}
          <Box sx={{ minWidth: "80px", maxWidth: "120px", textAlign: "center", overflow: "hidden", display: "flex", flexDirection: "column", gap: "12px" }}>
            <Typography 
              variant="body1" 
              sx={{ 
                fontFamily: "DM Sans",
                fontWeight: 500,
                fontSize: "14px",
                lineHeight: "100%",
                letterSpacing: "0px",
                textTransform: "capitalize",
                color: "#756DE9"
              }}
            >
              Location
            </Typography>
            <Tooltip title={location} arrow placement="top">
              <Typography 
                variant="body2" 
                sx={{ 
                  fontFamily: "DM Sans",
                  fontWeight: 500,
                  fontStyle: "normal",
                  fontSize: "14px",
                  lineHeight: "100%",
                  letterSpacing: "0px",
                  color: "#222222",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  width: "100%"
                }}
              >
                {location}
              </Typography>
            </Tooltip>
          </Box>

          {/* Vertical Divider */}
          <Divider orientation="vertical" flexItem sx={{ height: "40px", mx: 1 }} />

          {/* Experience */}
          <Box sx={{ minWidth: "80px", maxWidth: "120px", textAlign: "center", overflow: "hidden", display: "flex", flexDirection: "column", gap: "12px" }}>
            <Typography 
              variant="body1" 
              sx={{ 
                fontFamily: "DM Sans",
                fontWeight: 500,
                fontSize: "14px",
                lineHeight: "100%",
                letterSpacing: "0px",
                textTransform: "capitalize",
                color: "#756DE9"
              }}
            >
              Experience
            </Typography>
            <Tooltip title={experienceRange} arrow placement="top">
              <Typography 
                variant="body2" 
                sx={{ 
                  fontFamily: "DM Sans",
                  fontWeight: 500,
                  fontStyle: "normal",
                  fontSize: "14px",
                  lineHeight: "100%",
                  letterSpacing: "0px",
                  color: "#222222",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  width: "100%"
                }}
              >
                {experienceRange}
              </Typography>
            </Tooltip>
          </Box>

          {/* Vertical Divider */}
          <Divider orientation="vertical" flexItem sx={{ height: "40px", mx: 1 }} />

          {/* Qualification */}
          <Box sx={{ minWidth: "80px", maxWidth: "120px", textAlign: "center", overflow: "hidden", display: "flex", flexDirection: "column", gap: "12px" }}>
            <Typography 
              variant="body1" 
              sx={{ 
                fontFamily: "DM Sans",
                fontWeight: 500,
                fontSize: "14px",
                lineHeight: "100%",
                letterSpacing: "0px",
                textTransform: "capitalize",
                color: "#756DE9"
              }}
            >
              Qualification
            </Typography>
            <Tooltip title={preferredQualification} arrow placement="top">
              <Typography 
                variant="body2" 
                sx={{ 
                  fontFamily: "DM Sans",
                  fontWeight: 500,
                  fontStyle: "normal",
                  fontSize: "14px",
                  lineHeight: "100%",
                  letterSpacing: "0px",
                  color: "#222222",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  width: "100%"
                }}
              >
                {preferredQualification}
              </Typography>
            </Tooltip>
          </Box>

          {/* Vertical Divider */}
          <Divider orientation="vertical" flexItem sx={{ height: "40px", mx: 1 }} />

          {/* CTC */}
          <Box sx={{ minWidth: "80px", maxWidth: "120px", textAlign: "center", overflow: "hidden", display: "flex", flexDirection: "column", gap: "12px" }}>
            <Typography 
              variant="body1" 
              sx={{ 
                fontFamily: "DM Sans",
                fontWeight: 500,
                fontSize: "14px",
                lineHeight: "100%",
                letterSpacing: "0px",
                textTransform: "capitalize",
                color: "#756DE9"
              }}
            >
              CTC
            </Typography>
            <Tooltip title={ctcRange} arrow placement="top">
              <Typography 
                variant="body2" 
                sx={{ 
                  fontFamily: "DM Sans",
                  fontWeight: 500,
                  fontStyle: "normal",
                  fontSize: "14px",
                  lineHeight: "100%",
                  letterSpacing: "0px",
                  color: "#222222",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  width: "100%"
                }}
              >
                {ctcRange}
              </Typography>
            </Tooltip>
          </Box>

          {/* Vertical Divider */}
          <Divider orientation="vertical" flexItem sx={{ height: "40px", mx: 1 }} />

          {/* Personality */}
          <Box sx={{ minWidth: "80px", maxWidth: "120px", textAlign: "center", overflow: "hidden", display: "flex", flexDirection: "column", gap: "12px" }}>
            <Typography 
              variant="body1" 
              sx={{ 
                fontFamily: "DM Sans",
                fontWeight: 500,
                fontSize: "14px",
                lineHeight: "100%",
                letterSpacing: "0px",
                textTransform: "capitalize",
                color: "#756DE9"
              }}
            >
              Personality
            </Typography>
            <Tooltip title={personalityType} arrow placement="top">
              <Typography 
                variant="body2" 
                sx={{ 
                  fontFamily: "DM Sans",
                  fontWeight: 500,
                  fontStyle: "normal",
                  fontSize: "14px",
                  lineHeight: "100%",
                  letterSpacing: "0px",
                  color: "#222222",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  width: "100%"
                }}
              >
                {personalityType}
              </Typography>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      {/* Action Button - Always show */}
      <Box sx={{ flexShrink: 0 }}>
        <Button 
          onClick={handleEditJob}
          sx={{
            color: "white",
            backgroundColor: "#756DE9",
            borderRadius: "20px",
            height: "38px",
            fontSize: "15px",
            fontFamily: "DM Sans",
            whiteSpace: "nowrap",
            "&:hover": {
              backgroundColor: "#7e76efff",
            }
          }}
        >
          {profileId ? "View Job Post" : "View/Edit Job Post"}
        </Button>
      </Box>

    </Box>
  );
};

export default VideoDetailheader;

